import type { EmergencyCall } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import type {
  CallQueue,
  EmergencyCallResult,
  SearchResultItem,
} from '../../GlobalMapSearch.types';

interface RunCallsSearchArgs {
  text: string;
  callQueue: CallQueue | undefined;
  /** Digits-only normalization of `text`, when the classifier produced one. */
  normalizedPhone: string | null;
  collected: SearchResultItem[];
  flush: () => void;
}

/** Strip everything except digits — phone numbers come in many formats. */
const onlyDigits = (s: string): string => s.replace(/\D/g, '');

/** Hard cap on how deep we'll recurse into a single ADR payload. */
const MAX_ADR_DEPTH = 8;
/** Hard cap on how many string leaves we'll inspect per call to bound work. */
const MAX_ADR_LEAVES_PER_CALL = 250;

interface AdrLeaf {
  org: string;
  orgTitle: string;
  fieldPath: string;
  value: string;
}

/**
 * Visit every string-ish leaf inside `value`, tagging each with the
 * dot-joined object path that produced it. Numeric array indices are
 * dropped from the displayed path so users see e.g. `location.street`
 * instead of `location.0.street`. The visitor returns `true` to stop the
 * walk early (used to honor the leaf cap and the first-match exit).
 */
function walkStringLeaves(
  value: unknown,
  path: string[],
  depth: number,
  visit: (leaf: { path: string; value: string }) => boolean,
): boolean {
  if (value == null || depth > MAX_ADR_DEPTH) return false;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return false;
    return visit({
      path: path.filter(p => !/^\d+$/.test(p)).join('.'),
      value: trimmed,
    });
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return visit({
      path: path.filter(p => !/^\d+$/.test(p)).join('.'),
      value: String(value),
    });
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      if (walkStringLeaves(value[i], [...path, String(i)], depth + 1, visit)) {
        return true;
      }
    }
    return false;
  }
  if (typeof value === 'object') {
    for (const k of Object.keys(value as Record<string, unknown>)) {
      if (
        walkStringLeaves(
          (value as Record<string, unknown>)[k],
          [...path, k],
          depth + 1,
          visit,
        )
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Walk every searchable string leaf inside a call's `additionalData` and
 * pass it to `visit`. Shape is `{ [org]: [{ display_schema, data, ... }] }`
 * per `EllingtonCardList`/`copyAllHelpers`; we walk the `data` payload of
 * each entry and tag every leaf with the org's display title for nicer
 * match surfacing. Stops early when `visit` returns `true` or when
 * `MAX_ADR_LEAVES_PER_CALL` leaves have been seen.
 */
function walkAdrLeaves(
  call: EmergencyCall,
  visit: (leaf: AdrLeaf) => boolean,
): void {
  const additionalData = call.additionalData as
    | Record<string, unknown>
    | undefined;
  if (!additionalData || typeof additionalData !== 'object') return;

  let visited = 0;
  for (const org of Object.keys(additionalData)) {
    const entries = additionalData[org];
    if (!Array.isArray(entries)) continue;
    for (const raw of entries) {
      if (!raw || typeof raw !== 'object') continue;
      const entry = raw as Record<string, unknown>;
      const schema = entry.display_schema as { title?: unknown } | undefined;
      const orgTitle =
        (typeof schema?.title === 'string' && schema.title.trim()) || org;

      const stopped = walkStringLeaves(entry.data, [], 0, leaf => {
        visited++;
        if (
          visit({
            org,
            orgTitle,
            fieldPath: leaf.path,
            value: leaf.value,
          })
        ) {
          return true;
        }
        return visited >= MAX_ADR_LEAVES_PER_CALL;
      });
      if (stopped || visited >= MAX_ADR_LEAVES_PER_CALL) return;
    }
  }
}

/**
 * Synchronous filter over the active emergency call queue.
 *
 * Match precedence per call:
 *   1. Phone-style match: cleaned digits of the query (or the classifier's
 *      normalized phone form) appear inside the call's `caller_id` digits.
 *   2. Plain substring match against the formatted `caller_id`.
 *   3. Substring match anywhere inside the call's ADR (`additionalData`).
 *      The first leaf hit wins so a single call only contributes one row;
 *      the matched field path + value are surfaced for the dropdown.
 *
 * Empty queues, queries shorter than 3 chars/digits, and queue entries
 * with no caller_id all short-circuit.
 */
export function runCallsSearch({
  text,
  callQueue,
  normalizedPhone,
  collected,
  flush,
}: RunCallsSearchArgs): void {
  if (!callQueue) return;

  const lowerQuery = text.trim().toLowerCase();
  if (lowerQuery.length < 3) return;

  const digitQueries = new Set<string>();
  const textDigits = onlyDigits(text);
  if (textDigits.length >= 3) digitQueries.add(textDigits);
  if (normalizedPhone) {
    const normDigits = onlyDigits(normalizedPhone);
    if (normDigits.length >= 3) digitQueries.add(normDigits);
  }

  let appended = false;
  for (const callId of Object.keys(callQueue)) {
    const call = callQueue[callId];
    if (!call) continue;
    // The queue is keyed by the caller's phone number, so `callId` is the
    // canonical caller id for both the digit and text matchers.
    const callerId = callId;
    const callerDigits = onlyDigits(callerId);

    let matchedField: string | undefined;
    let matchedText: string | undefined;

    const digitHit =
      callerDigits &&
      Array.from(digitQueries).some(q => callerDigits.includes(q));
    const callerTextHit =
      !digitHit && callerId.toLowerCase().includes(lowerQuery);

    if (!digitHit && !callerTextHit) {
      walkAdrLeaves(call, leaf => {
        if (!leaf.value.toLowerCase().includes(lowerQuery)) return false;
        matchedField = leaf.fieldPath
          ? `${leaf.orgTitle} · ${leaf.fieldPath}`
          : leaf.orgTitle;
        matchedText = leaf.value;
        return true;
      });
      if (!matchedField) continue;
    }

    const result: EmergencyCallResult = {
      callId,
      callerId,
      call,
      matchedField,
      matchedText,
    };
    collected.push({ type: 'call', data: result });
    appended = true;
  }

  if (appended) flush();
}
